/**
 * Phase 33 — Telegram-style swipe-left-to-reply gesture for chat bubbles.
 *
 * Tracks a horizontal touch drag, follows the finger with rubber-band
 * resistance, and fires `onSwipeReply()` if the user releases past
 * `triggerPx`. Returns:
 *   - `bindings`  — spread onto the swipeable container
 *   - `translateX` — the live translate-X (px, ≤ 0) to apply to the bubble
 *   - `progress`  — 0..1, how close the swipe is to the trigger threshold;
 *                   use to fade in a reply icon and gate haptics
 *
 * Why touch-only (no mouse): swipe-to-reply is a touch convention. Desktops
 * use the long-press menu's Reply item, not a drag gesture.
 *
 * Coexistence with `useLongPress`:
 *   - Both consume pointer events. We only engage the swipe path on the
 *     FIRST `touchmove` whose |dx| > |dy| AND |dx| > horizontalDeadzonePx.
 *   - Once engaged, we cancel the long-press timer (via the caller's
 *     `cancelLongPress` callback) and own the gesture until pointerup.
 *   - If the first move is vertical-dominant, we abort the swipe path and
 *     leave the long-press / scroll handling alone.
 *
 * iOS rubber-band: we cap horizontal travel at `maxPx` and apply a square-
 * root decay past `triggerPx` so the bubble visibly resists overshoot.
 *
 * Haptic feedback: when the gesture first crosses the trigger threshold,
 * we fire a single short vibration (10 ms) if the platform supports it.
 * Telegram does this and it makes the gesture feel intentional.
 */

import { useCallback, useRef, useState } from 'react';

export interface SwipeReplyOptions {
  /** Whether this row is allowed to be a reply target. When false the hook
   *  no-ops entirely — no translateX, no triggering. Gate on
   *  `message.telegramMessageId != null` (same gate as the modal Reply
   *  button). */
  enabled: boolean;
  /** Px the user must drag past for the gesture to qualify on release.
   *  Default 60. Below this on release, bubble springs back. */
  triggerPx?: number;
  /** Hard cap for visual translate-X (rubber-banded past triggerPx).
   *  Default 100. Stops the bubble from drifting off-screen. */
  maxPx?: number;
  /** Minimum |dx| before we commit to the swipe direction. Default 6.
   *  Smaller than this and we leave the gesture to long-press / scroll. */
  horizontalDeadzonePx?: number;
  /** Called when the user releases past `triggerPx`. */
  onSwipeReply: () => void;
  /** Optional: cancel the long-press timer once we commit to the swipe
   *  direction. Accepts either a no-arg cancel or the matching
   *  `useLongPress` instance's `onPointerCancel` handler. The hook calls
   *  it without an event, so the handler must tolerate a missing arg
   *  (useLongPress.onPointerCancel ignores its arg — safe to cast). */
  cancelLongPress?: (e?: React.PointerEvent<HTMLElement>) => void;
}

export interface SwipeReplyBindings {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
}

export interface SwipeReplyState {
  /** Live translate-X to apply to the bubble (always ≤ 0). */
  translateX: number;
  /** 0..1, |translateX| / triggerPx clamped. Drives the reply-icon fade. */
  progress: number;
  /** True once the gesture has crossed the trigger threshold this drag.
   *  Use to flip the icon to "active" color and avoid re-firing haptics. */
  armed: boolean;
}

export function useSwipeReply({
  enabled,
  triggerPx = 60,
  maxPx = 100,
  horizontalDeadzonePx = 6,
  onSwipeReply,
  cancelLongPress,
}: SwipeReplyOptions): SwipeReplyBindings & SwipeReplyState {
  const [translateX, setTranslateX] = useState(0);
  const [armed, setArmed] = useState(false);

  // Refs so the move handler doesn't re-bind on every state change.
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const committedRef = useRef<null | 'swipe' | 'abort'>(null);
  const armedRef = useRef(false);

  const reset = useCallback(() => {
    committedRef.current = null;
    armedRef.current = false;
    setTranslateX(0);
    setArmed(false);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      if (e.pointerType !== 'touch') return; // touch-only
      if (!e.isPrimary) return;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      committedRef.current = null;
      armedRef.current = false;
    },
    [enabled],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      if (e.pointerType !== 'touch') return;
      if (committedRef.current === 'abort') return;

      const dx = e.clientX - startXRef.current;
      const dy = e.clientY - startYRef.current;

      // Direction commit on the first move that escapes the deadzone.
      if (committedRef.current == null) {
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        if (absDx < horizontalDeadzonePx && absDy < horizontalDeadzonePx) return;
        // Vertical-dominant OR rightward drag → not our gesture. Abort so
        // the page can scroll (vertical) or the long-press wins (rightward
        // micro-jitter that doesn't qualify as a left swipe).
        if (absDy > absDx || dx >= 0) {
          committedRef.current = 'abort';
          return;
        }
        committedRef.current = 'swipe';
        cancelLongPress?.();
      }

      if (committedRef.current !== 'swipe') return;

      // Only follow left drags; clamp at the trigger threshold then apply
      // square-root decay so further pull rubber-bands toward maxPx.
      const leftPx = Math.min(0, dx); // dx is negative for left
      let translated: number;
      if (-leftPx <= triggerPx) {
        translated = leftPx;
      } else {
        const overflow = -leftPx - triggerPx;
        // sqrt decay: every additional 100 px of pull adds only ~10 px.
        const damped = Math.sqrt(overflow) * 3;
        translated = -(triggerPx + Math.min(damped, maxPx - triggerPx));
      }
      setTranslateX(translated);

      // Arming threshold: |translateX| >= triggerPx. Fires haptic exactly
      // once per drag when crossing in either direction (re-armable so the
      // user can drag past then back).
      const nowArmed = -translated >= triggerPx;
      if (nowArmed !== armedRef.current) {
        armedRef.current = nowArmed;
        setArmed(nowArmed);
        if (nowArmed && typeof navigator !== 'undefined' && navigator.vibrate) {
          try {
            navigator.vibrate(10);
          } catch {
            // Some browsers throw on user-activation gating; ignore.
          }
        }
      }
    },
    [enabled, triggerPx, maxPx, horizontalDeadzonePx, cancelLongPress],
  );

  const onPointerUp = useCallback(() => {
    if (committedRef.current === 'swipe' && armedRef.current) {
      onSwipeReply();
    }
    reset();
  }, [onSwipeReply, reset]);

  const onPointerCancel = useCallback(() => {
    reset();
  }, [reset]);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    translateX,
    progress: enabled ? Math.min(1, Math.abs(translateX) / triggerPx) : 0,
    armed,
  };
}
